import React, { useState } from "react";
import { Phone, MessageSquare, ExternalLink, Download, FileText, ShieldCheck, BookOpen, School } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Article } from "@/components/ArticleBody";
import { makeSafetyPlanPdf, makeSchoolPacket } from "@/lib/guidePdf";
import { ARTICLES, TEEN_FACTS } from "@/lib/articles";
import { ARTICLES_ES, TEEN_FACTS_ES } from "@/lib/articles.es";
import { RESOURCE_GROUPS_ES, FAQ_ES, RES_UI } from "@/lib/content.es";
import { useLang, LangToggle } from "@/lib/lang";
import { T } from "@/lib/i18n";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { RESOURCE_GROUPS, FAQ } from "@/lib/content";
import { IMG } from "@/lib/images";
import { toast } from "sonner";

export default function Resources() {
  const { lang } = useLang();
  const t = T[lang];
  const r = RES_UI[lang];
  const allGroups = lang === "es" ? RESOURCE_GROUPS_ES : RESOURCE_GROUPS;
  const faq = lang === "es" ? FAQ_ES : FAQ;
  const [activeIdx, setActiveIdx] = useState(0);
  const groups = [allGroups[activeIdx]];
  const guides = lang === "es" ? ARTICLES_ES : ARTICLES;

  const downloadPlan = async () => {
    await makeSafetyPlanPdf(lang);
    toast.success(t.planToast);
  };
  const downloadPacket = async () => {
    await makeSchoolPacket(guides, lang === "es" ? TEEN_FACTS_ES : TEEN_FACTS, lang);
    toast.success(t.packetToast);
  };

  return (
    <div data-testid="resources-page" data-i18n="manual">
      <PageHero
        eyebrow={r.eyebrow}
        title={r.title}
        subtitle={r.sub}
        image={IMG.caringHands}
        testid="resources-hero"
      >
        <button onClick={downloadPlan} className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold transition-all" data-testid="download-safety-plan-btn">
          <Download className="w-5 h-5" /> {r.planBtn}
        </button>
        <div className="mt-5"><LangToggle /></div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {allGroups.map((g, idx) => (
            <button key={RESOURCE_GROUPS[idx].category} onClick={() => setActiveIdx(idx)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${activeIdx === idx ? "bg-bsn-midnight text-bsn-parchment" : "bg-white text-bsn-sapphire card-tactile hover:bg-bsn-canvas"}`}
              data-testid={`resource-filter-${RESOURCE_GROUPS[idx].category.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
              {g.category}
            </button>
          ))}
        </div>

        <div className="space-y-12">
          {groups.map((g) => (
            <div key={g.category}>
              <h3 className="font-serif text-2xl font-semibold text-bsn-midnight mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-bsn-gold" /> {g.category}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((it, i) => (
                  <Reveal key={it.name} delay={i * 70}>
                    <div className="h-full bg-white rounded-2xl p-6 card-tactile flex flex-col" data-testid={`resource-card-${it.name.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
                      <h4 className="font-serif text-lg font-semibold text-bsn-midnight">{it.name}</h4>
                      <p className="text-sm text-bsn-sapphire/80 mt-1.5 flex-1">{it.detail}</p>
                      <a href={it.action} target={it.external ? "_blank" : undefined} rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-bsn-bronze hover:text-bsn-gold transition-colors">
                        {it.external ? <ExternalLink className="w-4 h-4" /> : it.action.startsWith("sms") ? <MessageSquare className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                        {it.cta}
                      </a>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-5xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <ShieldCheck className="w-9 h-9 text-bsn-gold mb-4" />
            <h3 className="font-serif text-2xl font-semibold text-bsn-midnight">{r.planTitle}</h3>
            <p className="mt-3 text-bsn-sapphire/80 leading-relaxed">{r.planSub}</p>
            <button onClick={downloadPlan} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-bsn-midnight text-bsn-parchment font-semibold hover:bg-bsn-navy transition-colors" data-testid="download-safety-plan-btn-2">
              <FileText className="w-5 h-5" /> {r.planBtn2}
            </button>
          </div>
          <img src={IMG.lakeDock} alt="Calm dock at dawn" className="rounded-2xl card-tactile w-full h-64 object-cover" />
        </div>
      </section>

      <section className="bg-bsn-midnight text-bsn-parchment" data-testid="help-articles">
        <div className="max-w-5xl mx-auto px-4 pt-16 pb-24 text-center">
          <BookOpen className="w-10 h-10 text-bsn-gold mx-auto mb-4" />
          <div className="mb-5"><LangToggle /></div>
          <p className="eyebrow text-bsn-gold mb-3">{t.eyebrow}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold leading-tight">{t.guidesTitle}</h2>
          <p className="mt-4 text-lg text-bsn-ice/80 max-w-2xl mx-auto">{t.guidesSub}</p>
          <p className="mt-6 inline-flex items-start gap-2.5 text-left text-base sm:text-lg font-semibold text-bsn-champagne bg-bsn-navy border-2 border-bsn-gold rounded-2xl px-5 py-3.5 max-w-2xl mx-auto" data-testid="help-disclaimer"><ShieldCheck className="w-6 h-6 text-bsn-gold shrink-0 mt-0.5" />{t.disclaimerLong}</p>
          <div className="mt-10 grid md:grid-cols-2 gap-5 text-left">
            {guides.map((a, i) => (
              <a key={a.id} href={`#guide-${a.id}`} className="group rounded-2xl bg-bsn-navy border-2 border-bsn-gold/60 hover:border-bsn-gold p-6 sm:p-7 flex gap-4 items-start" data-testid={`article-choose-${a.id}`}>
                <span className="w-11 h-11 rounded-full bg-bsn-gold text-bsn-midnight font-display font-bold text-lg flex items-center justify-center shrink-0">{i + 1}</span>
                <span>
                  <span className="block font-serif text-2xl font-semibold text-bsn-champagne leading-snug">{a.title}</span>
                  <span className="block mt-2 text-sm text-bsn-ice/75">{a.summary}</span>
                  <span className="mt-3 inline-block text-sm font-semibold text-bsn-gold">{t.readGuide}</span>
                </span>
              </a>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border-2 border-dashed border-bsn-gold/60 p-6 sm:p-7 flex flex-col sm:flex-row items-center gap-5 text-left" data-testid="school-packet-card">
            <School className="w-10 h-10 text-bsn-gold shrink-0" />
            <span className="flex-1">
              <span className="block font-serif text-2xl font-semibold text-bsn-champagne">{t.packetTitle}</span>
              <span className="block mt-1 text-sm text-bsn-ice/75">{t.packetSub}</span>
            </span>
            <button onClick={downloadPacket} className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold shrink-0" data-testid="download-school-packet-btn">
              <Download className="w-5 h-5" /> {t.packetBtn}
            </button>
          </div>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 -mt-12 space-y-10">
        {guides.map((a, i) => (
          <div key={a.id} id={`guide-${a.id}`} className="scroll-mt-24"><Article article={a} index={i} /></div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
        <SectionTitle eyebrow={r.faqEyebrow} title={r.faqTitle} />
        <Accordion type="single" collapsible className="space-y-3" data-testid="resources-faq">
          {faq.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`} className="bg-white rounded-xl px-5 card-tactile border-none">
              <AccordionTrigger className="font-serif text-lg text-bsn-midnight hover:no-underline text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-bsn-sapphire/80 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
